<template>
	<v-card elevation="0" outlined class="contract-status-card">
		<v-card-text class="pa-8" :class="{ 'text-center': !hasContracts }">
			<template v-if="!hasContracts">
				<h2 class="darkColor--text mb-8">Contract Status</h2>

				<div class="contract-empty">
				<div class="contract-icon-circle mx-auto mb-6">
					<v-icon color="#1E7C83" size="48">fas fa-file-lines</v-icon>
				</div>
				<h3 class="mb-3 font-weight-bold">No contract uploaded.</h3>
				<p class="grey--text text--darken-1 mb-6 contract-subtitle">
					Please upload the signed contract to begin supplier performance monitoring.
				</p>
				<v-btn
					v-if="canUploadContract"
					color="#1E7C83"
					dark
					depressed
					class="upload-btn px-8"
					@click="openUploadDialog(false)"
				>
					Upload Contract
				</v-btn>
				<p v-else class="grey--text text--darken-1 mb-0 contract-subtitle">
					Only users with the Logistic Manager or Logistic Contributor role can upload contracts.
				</p>
			</div>
			</template>

			<div v-else class="contract-uploaded">
				<div class="contract-header d-flex align-center mb-5">
					<h2 class="darkColor--text mb-0">Contract Status</h2>
					<v-btn
						v-if="canViewKpi"
						outlined
						color="#1E7C83"
						class="kpi-btn ml-3"
						depressed
						:disabled="!canEnterKpi"
						@click="openKpiDialog"
					>
						KPI evaluation
					</v-btn>
				</div>

				<div
					v-for="contract in paginatedContracts"
					:key="contract.id"
					class="contract-row"
				>
					<div class="contract-stats contract-stats--compact">
						<div class="contract-stat-card contract-stat-card--compact">
							<div class="stat-icon-wrap stat-icon-wrap--teal stat-icon-wrap--compact">
								<v-icon color="#1E7C83" size="12">fas fa-file-signature</v-icon>
							</div>
							<div class="stat-body">
								<p class="stat-label">Contract name</p>
								<p class="stat-value stat-value--name">{{ getContractNameDisplay(contract) }}</p>
							</div>
						</div>

						<div class="contract-stat-card contract-stat-card--compact">
							<div class="stat-icon-wrap stat-icon-wrap--teal stat-icon-wrap--compact">
								<v-icon color="#1E7C83" size="12">fas fa-calendar-days</v-icon>
							</div>
							<div class="stat-body">
								<p class="stat-label">Expiry date</p>
								<p class="stat-value">{{ getExpiryDateFormatted(contract) }}</p>
							</div>
						</div>

						<div
							class="contract-stat-card contract-stat-card--compact contract-stat-card--accent"
							:style="{
								borderColor: getStatusInfo(contract).dotColor,
								background: getStatusInfo(contract).cardBg,
							}"
						>
							<div
								class="stat-icon-wrap stat-icon-wrap--compact"
								:style="{
									backgroundColor: getStatusInfo(contract).iconBg,
								}"
							>
								<v-icon :color="getStatusInfo(contract).dotColor" size="12">fas fa-hourglass-half</v-icon>
							</div>
							<div class="stat-body">
								<p class="stat-label">Days remaining</p>
								<p class="stat-value stat-value--days">
									<span
										class="stat-number"
										:style="{ color: getStatusInfo(contract).textColor }"
									>{{ getDaysRemainingNumber(contract) }}</span>
									<span class="stat-unit">days</span>
								</p>
							</div>
						</div>

						<div
							class="contract-stat-card contract-stat-card--compact contract-stat-card--accent"
							:style="{
								borderColor: getStatusInfo(contract).dotColor,
								background: getStatusInfo(contract).cardBg,
							}"
						>
							<div
								class="stat-icon-wrap stat-icon-wrap--compact"
								:style="{
									backgroundColor: getStatusInfo(contract).iconBg,
								}"
							>
								<v-icon :color="getStatusInfo(contract).dotColor" size="12">fas fa-circle-check</v-icon>
							</div>
							<div class="stat-body">
								<p class="stat-label">Status</p>
								<span
									class="status-pill status-pill--large"
									:style="{
										backgroundColor: getStatusInfo(contract).pillBg,
										color: getStatusInfo(contract).textColor,
										borderColor: getStatusInfo(contract).dotColor,
									}"
								>
									<span
										class="status-dot"
										:style="{ backgroundColor: getStatusInfo(contract).dotColor }"
									></span>
									{{ getStatusInfo(contract).label }}
								</span>
							</div>
						</div>
					</div>

					<div class="contract-row-actions">
						<v-btn
							color="#1E7C83"
							dark
							depressed
							class="upload-btn contract-row-btn"
							@click="$emit('download', contract)"
						>
							View contract
						</v-btn>
						<v-btn
							v-if="canUploadContract"
							color="#1E7C83"
							outlined
							class="upload-btn contract-row-btn"
							@click="openUploadDialog(true, contract)"
						>
							Replace contract
						</v-btn>
					</div>
				</div>

				<div v-if="canUploadContract" class="contract-add-more text-center mt-4 mb-2">
					<v-btn
						color="#1E7C83"
						outlined
						class="upload-btn px-8"
						:disabled="!canAddMoreContracts"
						@click="openUploadDialog(false)"
					>
						Add more contract
					</v-btn>
					<p v-if="!canAddMoreContracts" class="contract-limit-note mb-0 mt-2">
						Maximum of {{ maxContracts }} contracts reached.
					</p>
				</div>

				<v-data-footer
					v-if="contracts.length > 2"
					class="contract-data-footer"
					:options.sync="contractFooterOptions"
					:pagination="contractFooterPagination"
					:items-per-page-options="[2, 3, 4, 5]"
					:show-current-page="false"
					items-per-page-text="Rows per page:"
				>
					<template #page-text="{ pageStart, pageStop, itemsLength }">
						{{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
					</template>
				</v-data-footer>

				<div class="status-legend mt-4">
					<p class="status-legend-title mb-4">Status</p>
					<div class="status-legend-grid">
						<div
							v-for="item in statusLegend"
							:key="item.key"
							class="status-legend-item"
						>
							<div class="d-flex align-center justify-center mb-1">
								<span
									class="status-dot mr-2"
									:style="{ backgroundColor: item.dotColor }"
								></span>
								<span class="status-legend-label">{{ item.label }}</span>
							</div>
							<p class="status-legend-desc mb-0">{{ item.description }}</p>
						</div>
					</div>
				</div>
			</div>
		</v-card-text>

		<v-dialog v-model="showUploadDialog" max-width="520" persistent>
			<v-card class="upload-dialog-card">
				<v-card-title class="justify-center pt-8 pb-2">
					<h2 class="upload-dialog-title">{{ isReplaceMode ? "Replace Contract" : "Upload Contract" }}</h2>
				</v-card-title>
				<v-card-text class="px-8 pb-2">
					<input
						ref="fileInput"
						type="file"
						accept=".pdf,application/pdf"
						hidden
						@change="onFileSelected"
					/>
					<div
						class="upload-dropzone mb-6"
						:class="{ 'upload-dropzone--selected': selectedFile }"
						@click="triggerFileSelect"
						@dragover.prevent
						@drop.prevent="onFileDrop"
					>
						<div
							class="upload-dropzone-icon mx-auto mb-2"
							:class="{ 'upload-dropzone-icon--success': selectedFile }"
						>
							<v-icon
								:color="selectedFile ? '#ffffff' : '#1E7C83'"
								size="28"
							>
								{{ selectedFile ? 'fas fa-check' : 'fas fa-cloud-arrow-up' }}
							</v-icon>
						</div>
						<p class="upload-dropzone-title mb-1">Upload contract PDF</p>
						<p class="upload-dropzone-subtitle mb-0">
							{{ dropzoneSubtitle }}
						</p>
					</div>

					<label class="field-label">Contract name</label>
					<v-text-field
						v-model="form.contractName"
						outlined
						dense
						hide-details
						class="mb-4"
						placeholder="e.g. Supply agreement 2026"
					/>

					<label class="field-label">Contact name</label>
					<v-text-field
						v-model="form.contactName"
						outlined
						dense
						hide-details
						class="mb-4"
						placeholder="e.g. Maya Rodriguez"
					/>

					<label class="field-label">Expiry date</label>
					<v-menu
						v-model="expiryDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="expiryDateDisplay"
								outlined
								dense
								hide-details
								readonly
								class="mb-4 expiry-date-field"
								placeholder="DD / MM / YYYY"
								append-icon="fas fa-calendar"
								v-bind="attrs"
								v-on="on"
							/>
						</template>
						<v-date-picker
							:value="form.expiryDate"
							color="primary"
							:min="minExpiryDate"
							@input="onExpiryDateSelected"
						/>
					</v-menu>

					<label class="field-label">Point of contact</label>
					<v-text-field
						v-model="form.pointOfContact"
						outlined
						dense
						hide-details
						class="mb-2"
						placeholder="e.g. maya.rodriguez@acme.com"
					/>
				</v-card-text>
				<v-card-actions class="upload-dialog-actions justify-center pb-8 pt-4">
					<v-btn
						text
						class="mr-2"
						:disabled="uploading"
						@click="closeUploadDialog"
					>
						Cancel
					</v-btn>
					<v-btn
						color="#1E7C83"
						dark
						depressed
						class="upload-btn save-contract-btn px-10"
						:loading="uploading"
						:disabled="!canSave || uploading"
						@click="saveContract"
					>
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="showKpiDialog" max-width="520" persistent>
			<v-card class="upload-dialog-card">
				<v-card-title class="kpi-dialog-header pt-6 pb-2 px-4">
					<div class="kpi-dialog-header-spacer"></div>
					<h2 class="upload-dialog-title mb-0">KPI evaluation</h2>
					<v-btn
						icon
						small
						class="kpi-dialog-close"
						aria-label="Close"
						@click="closeKpiDialog"
					>
						<v-icon size="20">fas fa-times</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="px-8 pb-2">
					<label class="field-label">Date</label>
					<v-menu
						v-model="kpiDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="kpiDateDisplay"
								outlined
								dense
								hide-details="auto"
								readonly
								class="mb-4"
								placeholder="DD / MM / YYYY"
								append-icon="fas fa-calendar"
								:error-messages="kpiSubmitted ? kpiDateError : []"
								v-bind="attrs"
								v-on="on"
							/>
						</template>
						<v-date-picker
							:value="kpiForm.date"
							color="primary"
							@input="onKpiDateSelected"
						/>
					</v-menu>

					<label class="field-label">Delivery KPI value (1-5)</label>
					<v-text-field
						:value="kpiForm.deliveryKpi"
						outlined
						dense
						hide-details="auto"
						class="mb-1"
						placeholder="e.g. 4"
						inputmode="numeric"
						:error-messages="kpiFieldError('deliveryKpi')"
						@keydown="blockDecimalInput"
						@input="onKpiFieldInput('deliveryKpi', $event)"
					/>
					<p class="kpi-scale-hint mb-4">5 Excellent · 4 Good · 3 Average · 2 Poor · 1 Very Poor</p>

					<label class="field-label">Quality KPI value (1-5)</label>
					<v-text-field
						:value="kpiForm.qualityKpi"
						outlined
						dense
						hide-details="auto"
						class="mb-4"
						placeholder="e.g. 4"
						inputmode="numeric"
						:error-messages="kpiFieldError('qualityKpi')"
						@keydown="blockDecimalInput"
						@input="onKpiFieldInput('qualityKpi', $event)"
					/>
					<p class="kpi-scale-hint mb-4">5 Excellent · 4 Good · 3 Average · 2 Poor · 1 Very Poor</p>

					<label class="field-label">Support KPI value (1-5)</label>
					<v-text-field
						:value="kpiForm.supportKpi"
						outlined
						dense
						hide-details="auto"
						class="mb-4"
						placeholder="e.g. 5"
						inputmode="numeric"
						:error-messages="kpiFieldError('supportKpi')"
						@keydown="blockDecimalInput"
						@input="onKpiFieldInput('supportKpi', $event)"
					/>
					<p class="kpi-scale-hint mb-4">5 Excellent · 4 Good · 3 Average · 2 Poor · 1 Very Poor</p>

					<label class="field-label">Comment</label>
					<v-textarea
						v-model="kpiForm.comment"
						outlined
						dense
						hide-details="auto"
						rows="3"
						class="mb-2"
						placeholder="Add any notes on supplier performance this period"
					/>
				</v-card-text>
				<v-card-actions class="kpi-dialog-actions justify-center px-8 pb-8 pt-2">
					<div class="kpi-action-buttons">
						<div class="d-flex kpi-action-row mb-3">
							<v-btn
								outlined
								color="#1E7C83"
								class="upload-btn kpi-action-btn mr-3"
								:loading="kpiSaving"
								:disabled="kpiSaving"
								@click="saveKpiEvaluation('draft')"
							>
								Save as draft
							</v-btn>
							<v-tooltip top max-width="280">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										v-on="on"
										color="#1E7C83"
										dark
										depressed
										class="upload-btn kpi-action-btn"
										:loading="kpiSaving"
										:disabled="kpiSaving"
										@click="saveKpiEvaluation('review')"
									>
										Review and save
									</v-btn>
								</template>
								<span>After you click here, your supplier cannot see the KPI values.</span>
							</v-tooltip>
						</div>
						<v-tooltip top max-width="320">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									v-bind="attrs"
									v-on="on"
									outlined
									color="#1E7C83"
									class="upload-btn kpi-action-btn kpi-action-btn--share"
									block
									:loading="kpiSaving"
									:disabled="kpiSaving"
									@click="saveKpiEvaluation('share')"
								>
									<v-icon left small>fas fa-share-nodes</v-icon>
									Save and share with supplier
								</v-btn>
							</template>
							<span>After you click this, your supplier gets a notification about their KPI performance.</span>
						</v-tooltip>
					</div>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
const MAX_CONTRACTS = 5

export default {
	name: "ContractStatus",
	props: {
		contracts: {
			type: Array,
			default: () => [],
		},
		uploading: {
			type: Boolean,
			default: false,
		},
		kpiEvaluation: {
			type: Object,
			default: null,
		},
		kpiSaving: {
			type: Boolean,
			default: false,
		},
		canViewKpi: {
			type: Boolean,
			default: false,
		},
		canEnterKpi: {
			type: Boolean,
			default: false,
		},
		canUploadContract: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			showUploadDialog: false,
			showKpiDialog: false,
			expiryDateMenu: false,
			kpiDateMenu: false,
			kpiSubmitted: false,
			kpiRequireAll: true,
			kpiPendingClose: false,
			selectedFile: null,
			isReplaceMode: false,
			replacingContract: null,
			maxContracts: MAX_CONTRACTS,
			contractFooterOptions: {
				page: 1,
				itemsPerPage: 2,
			},
			form: {
				contractName: "",
				contactName: "",
				expiryDate: "",
				pointOfContact: "",
			},
			kpiForm: {
				date: "",
				deliveryKpi: "",
				qualityKpi: "",
				supportKpi: "",
				comment: "",
			},
		}
	},
	computed: {
		hasContracts() {
			return this.contracts.length > 0
		},
		canAddMoreContracts() {
			return this.contracts.length < MAX_CONTRACTS
		},
		contractFooterPagination() {
			const itemsLength = this.contracts.length
			const itemsPerPage = this.contractFooterOptions.itemsPerPage
			const page = this.contractFooterOptions.page
			const pageCount = itemsLength === 0 ? 1 : Math.ceil(itemsLength / itemsPerPage)
			return {
				page,
				itemsPerPage,
				pageStart: itemsLength === 0 ? 0 : (page - 1) * itemsPerPage,
				pageStop: itemsLength === 0 ? 0 : Math.min(page * itemsPerPage, itemsLength),
				pageCount,
				itemsLength,
			}
		},
		paginatedContracts() {
			const { page, itemsPerPage } = this.contractFooterOptions
			const start = (page - 1) * itemsPerPage
			return this.contracts.slice(start, start + itemsPerPage)
		},
		statusLegend() {
			return [
				{
					key: "active",
					label: "Active",
					dotColor: "#2e7d32",
					description: "More than 90 days remaining",
				},
				{
					key: "renewal",
					label: "Renewal due",
					dotColor: "#f9a825",
					description: "30–90 days remaining",
				},
				{
					key: "expiring",
					label: "Expiring soon",
					dotColor: "#ef6c00",
					description: "Fewer than 30 days remaining",
				},
				{
					key: "expired",
					label: "Expired",
					dotColor: "#c62828",
					description: "Expiry date has passed",
				},
			]
		},
		expiryDateDisplay() {
			if (!this.form.expiryDate) {
				return ""
			}
			return this.$moment(this.form.expiryDate, "YYYY-MM-DD").format("DD / MM / YYYY")
		},
		minExpiryDate() {
			return this.$moment().format("YYYY-MM-DD")
		},
		canSave() {
			const fieldsValid = !!this.form.contractName.trim()
				&& !!this.form.contactName.trim()
				&& !!this.form.expiryDate
				&& !!this.form.pointOfContact.trim()
			if (!fieldsValid) {
				return false
			}
			if (this.isReplaceMode) {
				return !!this.selectedFile || this.hasMetadataChanges
			}
			return !!this.selectedFile
		},
		hasFormChanges() {
			if (this.selectedFile) {
				return true
			}
			return this.hasMetadataChanges
		},
		hasMetadataChanges() {
			if (!this.isReplaceMode) {
				return false
			}
			if (!this.replacingContract) {
				return true
			}
			const originalExpiry = this.replacingContract.expiryDate
				? this.$moment(this.replacingContract.expiryDate).format("YYYY-MM-DD")
				: ""
			return this.form.contractName.trim() !== (this.replacingContract.contractName || "")
				|| this.form.contactName.trim() !== (this.replacingContract.contactName || "")
				|| this.form.pointOfContact.trim() !== (this.replacingContract.pointOfContact || "")
				|| this.form.expiryDate !== originalExpiry
		},
		dropzoneSubtitle() {
			if (this.selectedFile) {
				return this.selectedFile.name
			}
			if (this.isReplaceMode && this.replacingContract?.filename) {
				return `Current file: ${this.replacingContract.filename}. Click to upload a new .pdf (optional)`
			}
			return "Click to browse or drag and drop a .pdf file"
		},
		kpiDateDisplay() {
			if (!this.kpiForm.date) {
				return ""
			}
			return this.$moment(this.kpiForm.date, "YYYY-MM-DD").format("DD / MM / YYYY")
		},
		kpiDateError() {
			if (!this.kpiSubmitted || this.kpiForm.date) {
				return ""
			}
			return "Date is required"
		},
	},
	methods: {
		getContractNameDisplay(contract) {
			return contract.contractName || contract.filename || "—"
		},
		getExpiryDateFormatted(contract) {
			if (!contract?.expiryDate) {
				return "—"
			}
			return this.$moment.utc(contract.expiryDate).format("D MMM YYYY")
		},
		getDaysRemaining(contract) {
			if (!contract?.expiryDate) {
				return null
			}
			return this.$moment.utc(contract.expiryDate)
				.startOf("day")
				.diff(this.$moment.utc().startOf("day"), "days")
		},
		getDaysRemainingNumber(contract) {
			const days = this.getDaysRemaining(contract)
			if (days === null) {
				return "—"
			}
			if (days < 0) {
				return "0"
			}
			return String(days)
		},
		getStatusInfo(contract) {
			const days = this.getDaysRemaining(contract)
			if (days === null) {
				return {
					key: "unknown",
					label: "Unknown",
					dotColor: "#757575",
					pillBg: "#f5f5f5",
					cardBg: "#fafafa",
					iconBg: "#eeeeee",
					textColor: "#616161",
				}
			}
			if (days < 0) {
				return {
					key: "expired",
					label: "Expired",
					dotColor: "#c62828",
					pillBg: "#ffebee",
					cardBg: "#fff5f5",
					iconBg: "#ffcdd2",
					textColor: "#c62828",
				}
			}
			if (days < 30) {
				return {
					key: "expiring",
					label: "Expiring soon",
					dotColor: "#ef6c00",
					pillBg: "#fff3e0",
					cardBg: "#fff8f0",
					iconBg: "#ffe0b2",
					textColor: "#ef6c00",
				}
			}
			if (days <= 90) {
				return {
					key: "renewal",
					label: "Renewal due",
					dotColor: "#f9a825",
					pillBg: "#fffde7",
					cardBg: "#fffdf5",
					iconBg: "#fff9c4",
					textColor: "#f57f17",
				}
			}
			return {
				key: "active",
				label: "Active",
				dotColor: "#2e7d32",
				pillBg: "#e8f5e9",
				cardBg: "#f3faf4",
				iconBg: "#c8e6c9",
				textColor: "#2e7d32",
			}
		},
		openUploadDialog(isReplace = false, contract = null) {
			if (!this.canUploadContract) {
				this.$emit("upload-error", "Only users with the Logistic Manager or Logistic Contributor role can upload contracts.")
				return
			}
			if (!isReplace && !this.canAddMoreContracts) {
				this.$emit("upload-error", `Maximum of ${MAX_CONTRACTS} contracts allowed.`)
				return
			}
			this.isReplaceMode = isReplace
			this.replacingContract = isReplace ? contract : null
			this.selectedFile = null
			if (isReplace && contract) {
				this.form.contractName = contract.contractName || ""
				this.form.contactName = contract.contactName || ""
				this.form.pointOfContact = contract.pointOfContact || ""
				this.form.expiryDate = contract.expiryDate
					? this.$moment(contract.expiryDate).format("YYYY-MM-DD")
					: ""
			} else {
				this.resetForm()
			}
			this.showUploadDialog = true
		},
		closeUploadDialog() {
			this.showUploadDialog = false
			this.expiryDateMenu = false
			this.isReplaceMode = false
			this.replacingContract = null
			this.selectedFile = null
			this.resetForm()
		},
		resetForm() {
			this.form = {
				contractName: "",
				contactName: "",
				expiryDate: "",
				pointOfContact: "",
			}
		},
		triggerFileSelect() {
			this.$refs.fileInput.click()
		},
		onFileSelected(event) {
			const file = event.target.files && event.target.files[0]
			event.target.value = ""
			if (file) {
				this.setSelectedFile(file)
			}
		},
		onFileDrop(event) {
			const file = event.dataTransfer.files && event.dataTransfer.files[0]
			if (file) {
				this.setSelectedFile(file)
			}
		},
		setSelectedFile(file) {
			if (!/\.pdf$/i.test(file.name)) {
				return
			}
			const maxBytes = 50 * 1024 * 1024
			if (file.size > maxBytes) {
				this.selectedFile = null
				this.$emit("upload-error", "Contract PDF is too large. Maximum file size is 50 MB.")
				return
			}
			this.selectedFile = file
			if (!this.form.contractName.trim()) {
				this.form.contractName = file.name.replace(/\.pdf$/i, "")
			}
		},
		onExpiryDateSelected(value) {
			this.form.expiryDate = value
			this.expiryDateMenu = false
		},
		saveContract() {
			if (!this.canSave) {
				return
			}
			if (!this.$moment(this.form.expiryDate, "YYYY-MM-DD", true).isValid()) {
				return
			}
			this.$emit("upload", {
				file: this.selectedFile,
				isReplace: this.isReplaceMode,
				replaceContractId: this.replacingContract?.id || null,
				contractName: this.form.contractName.trim(),
				contactName: this.form.contactName.trim(),
				pointOfContact: this.form.pointOfContact.trim(),
				expiryDate: this.form.expiryDate,
			})
		},
		openKpiDialog() {
			if (!this.canEnterKpi) {
				this.$emit("kpi-access-denied")
				return
			}
			this.kpiSubmitted = false
			this.kpiRequireAll = true
			this.kpiPendingClose = false
			this.prefillKpiForm()
			this.showKpiDialog = true
		},
		closeKpiDialog() {
			this.showKpiDialog = false
			this.kpiDateMenu = false
			this.kpiSubmitted = false
			this.kpiRequireAll = true
			this.kpiPendingClose = false
			this.resetKpiForm()
		},
		prefillKpiForm() {
			if (!this.kpiEvaluation) {
				this.resetKpiForm()
				return
			}
			this.kpiForm = {
				date: this.kpiEvaluation.evaluationDate
					? this.$moment(this.kpiEvaluation.evaluationDate).format("YYYY-MM-DD")
					: "",
				deliveryKpi: this.kpiEvaluation.deliveryKpi ?? "",
				qualityKpi: this.kpiEvaluation.qualityKpi ?? "",
				supportKpi: this.kpiEvaluation.supportKpi ?? "",
				comment: this.kpiEvaluation.comment || "",
			}
		},
		resetKpiForm() {
			this.kpiForm = {
				date: "",
				deliveryKpi: "",
				qualityKpi: "",
				supportKpi: "",
				comment: "",
			}
		},
		onKpiDateSelected(value) {
			this.kpiForm.date = value
			this.kpiDateMenu = false
		},
		blockDecimalInput(event) {
			if ([".", ",", "e", "E", "-", "+"].includes(event.key)) {
				event.preventDefault()
			}
		},
		onKpiFieldInput(field, value) {
			if (value === "" || value === null || value === undefined) {
				this.kpiForm[field] = ""
				return
			}
			const raw = String(value).trim()
			if (!/^\d+$/.test(raw)) {
				this.kpiForm[field] = this.sanitizeKpiValue(this.kpiForm[field])
				return
			}
			this.kpiForm[field] = this.sanitizeKpiValue(raw)
		},
		sanitizeKpiValue(value) {
			if (value === "" || value === null || value === undefined) {
				return ""
			}
			const parsed = parseInt(String(value), 10)
			if (isNaN(parsed)) {
				return ""
			}
			if (parsed > 5) {
				return 5
			}
			if (parsed < 1) {
				return ""
			}
			return parsed
		},
		isValidKpiValue(value, required = false) {
			if (value === "" || value === null || value === undefined) {
				return !required
			}
			const parsed = parseInt(String(value), 10)
			return Number.isInteger(parsed) && parsed >= 1 && parsed <= 5
		},
		kpiFieldError(field) {
			if (!this.kpiSubmitted) {
				return []
			}
			const value = this.kpiForm[field]
			if (value === "" || value === null || value === undefined) {
				return this.kpiRequireAll ? ["Enter a whole number from 1 to 5"] : []
			}
			if (!this.isValidKpiValue(value)) {
				return ["Enter a whole number from 1 to 5"]
			}
			return []
		},
		validateKpiForm(requireAll = true) {
			this.kpiRequireAll = requireAll
			this.kpiSubmitted = true
			const dateValid = !!this.kpiForm.date
			const kpiFields = ["deliveryKpi", "qualityKpi", "supportKpi"]
			const kpisValid = kpiFields.every((field) => this.isValidKpiValue(this.kpiForm[field], false))

			if (!dateValid || !kpisValid) {
				return false
			}
			if (!requireAll) {
				return true
			}
			return kpiFields.every((field) => this.isValidKpiValue(this.kpiForm[field], true))
		},
		buildKpiPayload(action) {
			return {
				action,
				date: this.kpiForm.date
					? this.$moment(this.kpiForm.date, "YYYY-MM-DD").toISOString()
					: null,
				deliveryKpi: this.kpiForm.deliveryKpi === "" ? null : this.sanitizeKpiValue(this.kpiForm.deliveryKpi),
				qualityKpi: this.kpiForm.qualityKpi === "" ? null : this.sanitizeKpiValue(this.kpiForm.qualityKpi),
				supportKpi: this.kpiForm.supportKpi === "" ? null : this.sanitizeKpiValue(this.kpiForm.supportKpi),
				comment: this.kpiForm.comment.trim(),
			}
		},
		saveKpiEvaluation(action) {
			const requireAll = action !== "draft"
			if (!this.validateKpiForm(requireAll)) {
				return
			}
			this.kpiPendingClose = true
			this.$emit("kpi-evaluation", this.buildKpiPayload(action))
		},
	},
	watch: {
		"contracts.length"(length) {
			const pageCount = Math.max(1, Math.ceil(length / this.contractFooterOptions.itemsPerPage))
			if (this.contractFooterOptions.page > pageCount) {
				this.contractFooterOptions = {
					...this.contractFooterOptions,
					page: pageCount,
				}
			}
		},
		uploading(isUploading, wasUploading) {
			if (wasUploading && !isUploading && this.showUploadDialog) {
				this.closeUploadDialog()
			}
		},
		kpiSaving(isSaving, wasSaving) {
			if (wasSaving && !isSaving && this.kpiPendingClose && this.showKpiDialog) {
				this.closeKpiDialog()
			}
		},
		kpiEvaluation(evaluation) {
			if (this.showKpiDialog && evaluation) {
				this.prefillKpiForm()
			}
		},
	},
}
</script>

<style scoped>
.contract-status-card,
.upload-dialog-card {
	border-radius: 10px;
	background: #fff;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
}

.upload-dialog-card >>> .v-card__text {
	overflow-y: auto;
}

.upload-dialog-actions {
	flex-shrink: 0;
	border-top: 1px solid #f0f0f0;
	background: #fff;
}

.save-contract-btn.v-btn--disabled {
	opacity: 1 !important;
	background-color: #b2cfcf !important;
	color: #ffffff !important;
}

.contract-icon-circle {
	width: 120px;
	height: 120px;
	border-radius: 50%;
	background-color: #e8f4f3;
	display: flex;
	align-items: center;
	justify-content: center;
}

.contract-subtitle {
	max-width: 420px;
	margin-left: auto;
	margin-right: auto;
	line-height: 1.5;
}

.upload-btn {
	text-transform: none;
	font-weight: 600;
	letter-spacing: 0.02em;
}

.upload-dialog-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a1a1a;
}

.upload-dropzone {
	border: 2px dashed #1E7C83;
	border-radius: 10px;
	background: #eef8f6;
	padding: 28px 20px;
	text-align: center;
	cursor: pointer;
}

.upload-dropzone--selected {
	border-color: #4caf50;
	background: #f1faf3;
}

.upload-dropzone-icon {
	width: 52px;
	height: 52px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #e8f4f3;
}

.upload-dropzone-icon--success {
	background: #4caf50;
}

.upload-dropzone-title {
	font-weight: 700;
	color: #1a1a1a;
}

.upload-dropzone-subtitle {
	color: #666;
	font-size: 0.95rem;
}

.upload-dropzone--selected .upload-dropzone-subtitle {
	color: #2e7d32;
	font-weight: 600;
}

.field-label {
	display: block;
	font-weight: 700;
	color: #1a1a1a;
	margin-bottom: 8px;
}

.kpi-dialog-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.kpi-dialog-header-spacer {
	width: 36px;
	flex-shrink: 0;
}

.kpi-dialog-close {
	color: #616161 !important;
}

.kpi-scale-hint {
	color: #757575;
	font-size: 0.75rem;
	line-height: 1.4;
}

.kpi-dialog-actions {
	width: 100%;
	align-items: stretch !important;
}

.kpi-action-buttons {
	width: 100%;
}

.kpi-action-row {
	width: 100%;
}

.kpi-action-btn {
	flex: 1 1 0;
	min-width: 0;
}

.kpi-action-btn--share {
	flex: none;
	width: 100%;
	text-transform: none;
	font-weight: 600;
	letter-spacing: 0.02em;
	border-radius: 8px;
}

.contract-header h2 {
	font-size: 1.5rem;
	font-weight: 700;
}

.kpi-btn {
	text-transform: none;
	font-weight: 600;
	font-size: 0.875rem;
	letter-spacing: 0.02em;
	border-radius: 999px !important;
	height: 36px !important;
	padding: 0 16px !important;
}

.contract-stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	max-width: 640px;
	margin: 0 auto;
}

.contract-stats--compact {
	flex: 1;
	min-width: 0;
	max-width: none;
	margin: 0;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 2px;
	align-items: stretch;
}

.contract-row {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 6px;
}

.contract-row-actions {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 4px;
	flex-shrink: 0;
	width: 140px;
}

.contract-row-btn {
	min-width: 0 !important;
	width: 100%;
	height: 34px !important;
	padding: 0 12px !important;
}

.contract-stat-card--compact {
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 2px;
	border-radius: 6px;
	box-shadow: none;
	gap: 0;
	height: 100%;
	min-height: 54px;
	box-sizing: border-box;
	align-self: stretch;
}

.contract-stat-card--compact.contract-stat-card--accent {
	border-width: 1px;
}

.contract-stat-card--compact .stat-body {
	min-width: 0;
	width: 100%;
	line-height: 1;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	gap: 0;
	padding: 0;
}

.contract-stat-card--compact p {
	margin: 0;
	padding: 0;
}

.contract-stat-card--compact .stat-number {
	line-height: 1;
}

.stat-icon-wrap--compact {
	width: 16px;
	height: 16px;
	border-radius: 4px;
	margin: 0 0 1px;
	flex-shrink: 0;
}

.stat-value--name {
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.contract-limit-note {
	color: #757575;
	font-size: 0.8125rem;
}

.contract-data-footer {
	border: none;
	border-radius: 0;
	box-shadow: none;
	background: transparent;
	padding: 0 4px;
	margin-bottom: 16px;
}

.contract-data-footer >>> .v-data-footer__select .v-input {
	max-width: 56px;
	margin-top: 0;
	padding-top: 0;
}

.contract-data-footer >>> .v-data-footer__select .v-input__slot {
	min-height: 32px !important;
}

.contract-data-footer >>> .v-data-footer__select .v-select__selections {
	justify-content: center;
}

.contract-data-footer >>> .v-data-footer__pagination {
	margin-right: 16px;
}

.contract-stat-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 14px 10px 12px;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
	transition: box-shadow 0.2s ease;
}

.contract-stat-card:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.contract-stat-card--accent {
	border-width: 1.5px;
}

.stat-icon-wrap {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 8px;
}

.stat-icon-wrap--teal {
	background: #e8f4f3;
}

.stat-label {
	color: #757575;
	font-size: 0.6875rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 4px;
}

.stat-value {
	font-weight: 700;
	color: #1a1a1a;
	font-size: 0.9375rem;
	line-height: 1.2;
	margin-bottom: 0;
}

.stat-value--days {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 3px;
}

.stat-number {
	font-size: 1.375rem;
	font-weight: 800;
	line-height: 1;
}

.stat-unit {
	font-size: 0.8125rem;
	font-weight: 600;
	color: #616161;
}

.status-pill {
	display: inline-flex;
	align-items: center;
	padding: 6px 14px;
	border-radius: 999px;
	font-weight: 700;
	font-size: 0.875rem;
}

.status-pill--large {
	padding: 4px 10px;
	font-size: 0.75rem;
	border: 1px solid transparent;
}

.status-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
}

.status-pill .status-dot {
	margin-right: 6px;
}

.status-legend {
	border-top: 1px solid #e0e0e0;
	padding-top: 24px;
	text-align: center;
}

.status-legend-title {
	font-weight: 700;
	color: #1a1a1a;
	font-size: 0.95rem;
}

.status-legend-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 20px 32px;
	max-width: 560px;
	margin: 0 auto;
}

.status-legend-item {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.status-legend-label {
	font-weight: 700;
	color: #1a1a1a;
	font-size: 0.875rem;
}

.status-legend-desc {
	color: #757575;
	font-size: 0.8125rem;
	line-height: 1.4;
	padding-left: 0;
	text-align: center;
}

/* Compact card spacing — placed after base stat-* rules so cascade wins */
.contract-stat-card--compact .stat-icon-wrap {
	width: 16px;
	height: 16px;
	border-radius: 4px;
	margin: 0 0 1px;
}

.contract-stat-card--compact .stat-icon-wrap >>> .v-icon {
	margin: 0;
	padding: 0;
}

.contract-stat-card--compact .stat-label {
	margin: 0;
	line-height: 1;
}

.contract-stat-card--compact .stat-value {
	margin: 0;
	padding: 0;
	line-height: 1.1;
}

.contract-stat-card--compact .stat-value--days {
	justify-content: center;
	width: 100%;
	gap: 2px;
}

.contract-stat-card--compact .stat-label + .stat-value,
.contract-stat-card--compact .stat-label + .status-pill--large {
	margin-top: 1px;
}

.contract-stat-card--compact .status-pill--large {
	padding: 0 6px;
	margin: 0;
	line-height: 1.1;
}

@media (max-width: 960px) {
	.contract-row {
		flex-direction: column;
	}

	.contract-row-actions {
		flex-direction: row;
		width: 100%;
	}

	.contract-row-btn {
		flex: 1 1 0;
	}

	.contract-stats--compact {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.contract-stats {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 600px) {
	.status-legend-grid {
		grid-template-columns: 1fr;
	}

	.contract-actions {
		flex-direction: column;
		align-items: stretch;
	}

	.contract-actions .v-btn {
		margin-right: 0 !important;
		margin-bottom: 12px;
	}

	.contract-actions .v-btn:last-child {
		margin-bottom: 0;
	}
}
</style>
